import type { InviteDevice } from '@tg/shared';

/**
 * Device signals sent with the sign-up (owner decision №6): the server hashes them and uses them only to deny the
 * invite bonus between accounts of one device. No third-party service; spoofable by design (see
 * apps/server/src/social/invite.ts). `id` — a random per-browser id kept in localStorage; `fp` — traits of the
 * device (languages, screen, time zone, cores, touch points, platform); the server adds the User-Agent.
 */
const KEY = 'tg_device';
const ID_RE = /^[A-Za-z0-9_-]{16,64}$/;

function browserId(): string {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved && ID_RE.test(saved)) return saved;
  } catch {
    /* storage blocked: a fresh id per sign-up */
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const id = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  try {
    localStorage.setItem(KEY, id);
  } catch {
    /* ignore */
  }
  return id;
}

export function deviceSignals(): InviteDevice {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  const scr = typeof screen === 'undefined' ? undefined : screen;
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    /* ignore */
  }
  const fp = [
    (nav?.languages ?? []).join(','),
    scr ? `${scr.width}x${scr.height}x${scr.colorDepth}@${typeof devicePixelRatio === 'number' ? devicePixelRatio : 1}` : '',
    tz,
    nav?.hardwareConcurrency ?? '',
    nav?.maxTouchPoints ?? '',
    nav?.platform ?? '',
  ]
    .join('|')
    .slice(0, 512);
  return { id: browserId(), fp: fp || '-' };
}
